import type {ImageField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import type {FourGridSliceValues} from 'prismicio-types'
import {Container} from 'src/common/Container'

type ValuesVariationProps = {
  slice: FourGridSliceValues & {
    slice_type: string
    variation: string
  }
}

export const ValuesVariation = ({slice}: ValuesVariationProps) => (
  <Container
    data-slice-type={slice.slice_type}
    data-slice-variation={slice.variation}
    backgroundColor={slice.primary.background_color}
    className="justify-center !py-10 px-5 md:!py-20 md:px-10 lg:px-5 xl:px-0"
  >
    <div className="flex w-full max-w-[1200px] flex-col gap-6" data-aos="fade-up" data-aos-offset="0">
      <div className="mg:gap-6 flex flex-col gap-3">
        {isFilled.keyText(slice.primary.tag) && (
          <span className="text-button-s text-moss-blue">{slice.primary.tag}</span>
        )}
        {isFilled.richText(slice.primary.title) && (
          <PrismicRichText
            field={slice.primary.title}
            components={{
              heading2: ({children}) => (
                <h2 className="lg:text-tg-h2 text-32 leading-[normal] tracking-[-.32px] text-moss-black md:pb-[38px] lg:pb-10">
                  {children}
                </h2>
              ),
              strong: ({children}) => <span className="font-normal text-moss-grey-400">{children}</span>,
            }}
          />
        )}
      </div>
      {isFilled.group(slice.primary.cards) && (
        <div className="grid grid-cols-4 gap-6 md:grid-cols-12">
          {slice.primary.cards.map((card, index) => (
            <div
              key={`value-card-${index}`}
              className="col-span-4 flex flex-col gap-3 rounded-lg border border-moss-grey-150 bg-moss-white p-6 md:col-span-full md:flex-row md:gap-6 md:max-lg:p-[22px]  lg:col-span-6"
            >
              <IconGrid icon={card.icon} />
              <div className="flex flex-col gap-3 md:justify-between">
                <div className="flex flex-col gap-1.5">
                  <span className="text-button-s text-moss-black/[60%]">{String(index + 1).padStart(2, '0')}</span>
                  <h3 className="text-[28px] leading-[normal] text-moss-black">{card.title}</h3>
                </div>
                <p className="text-tg-body-l text-moss-black/[60%]">{card.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </Container>
)

const IconGrid = ({icon}: {icon: ImageField}) => (
  <div className="grid w-full grid-cols-[auto_100px_auto] grid-rows-[auto_100px_auto] max-md:aspect-[287/200] md:aspect-square md:w-[194px] md:min-w-[194px] md:grid-cols-[auto_87px_auto] md:grid-rows-[auto_87px_auto]">
    <div className="col-span-1 col-start-1 row-start-1 rounded-br-[4px] rounded-tl-[6px] border border-moss-grey-150" />
    <div className="col-span-1 col-start-2 row-start-1 ml-[-1px] rounded-b-[4px] border border-moss-grey-150" />
    <div className="col-span-1 col-start-3 row-start-1 ml-[-1px] rounded-bl-[4px] rounded-tr-[6px] border border-moss-grey-150" />

    <div className="col-span-1 col-start-1 row-start-2 mt-[-1px] rounded-r-[4px] border border-moss-grey-150" />
    <div className="col-span-1 col-start-2 row-start-2 ml-[-1px] mt-[-1px] flex items-center justify-center rounded-[4px] border border-moss-grey-150 bg-green-neon">
      <PrismicNextImage field={icon} className="size-10" />
    </div>
    <div className="col-span-1 col-start-3 row-start-2 ml-[-1px] mt-[-1px] rounded-l-[4px] border border-moss-grey-150" />

    <div className="col-span-1 col-start-1 row-start-3 mt-[-1px] rounded-bl-[6px] rounded-tr-[4px] border border-moss-grey-150" />
    <div className="col-span-1 col-start-2 row-start-3 ml-[-1px] mt-[-1px] rounded-t-[4px] border border-moss-grey-150" />
    <div className="col-span-1 col-start-3 row-start-3 ml-[-1px] mt-[-1px] rounded-br-[6px] rounded-tl-[4px] border border-moss-grey-150" />
  </div>
)
